import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Button, Grid, CircularProgress, Fade, Grow } from '@mui/material';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupIcon from '@mui/icons-material/Group';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { styled, keyframes } from '@mui/material/styles';
import { getDownloadURL, listAll, ref } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';

// Components
import Navbar from '../../../components/Navbar/Navbar';
import Footer from '../../../components/Footer/Footer';
import Sidebar from '../../../components/Sidebar/Sidebar';

// Assets
import cleverIcon from '../../../../assets/img/Clever-icon.png';
import classlinkIcon from '../../../../assets/img/classlink-icon.png';

// Utilities
import RemoteErrorLog from '../../../Utils/api';

// Styles
import '../../../scss/Form.scss';

// Floating animation (same as InitialChoice)
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 900,
  margin: '0 auto',
  position: 'relative',
  
  // Subtle background for the content area
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -20,
    left: -20,
    right: -20,
    bottom: -20,
    background: `
      radial-gradient(circle at 30% 70%, rgba(42, 146, 53, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 70% 30%, rgba(76, 175, 80, 0.03) 0%, transparent 50%)
    `,
    borderRadius: 24,
    zIndex: -1,
  }
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(6),
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  // match the provided snippet: responsive sizes, weight, gradient text and fallback
  fontSize: '1.75rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1.5),
  letterSpacing: '-0.02em',
  position: 'relative',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  // provide a readable fallback for browsers that don't support background-clip:text
  WebkitTextFillColor: theme.palette.text.secondary,

  [theme.breakpoints.up('md')]: {
    fontSize: '2.25rem',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
    borderRadius: 2,
  }
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  // tighter, consistent sizing and placement under the main title
  fontSize: '1rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    fontSize: '1.125rem',
  }
}));

const OptionCard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5, 4),
  height: '100%',
  minHeight: 480,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  borderRadius: 20,
  border: '1px solid rgba(42, 146, 53, 0.1)',
  background: `
    linear-gradient(145deg, 
      rgba(255, 255, 255, 0.95) 0%, 
      rgba(248, 250, 252, 0.95) 100%
    )
  `,
  backdropFilter: 'blur(10px)',
  overflow: 'hidden',
  transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
  boxShadow: `
    0 6px 24px rgba(42, 146, 53, 0.08),
    0 3px 12px rgba(0, 0, 0, 0.04)
  `,
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(42, 146, 53, 0.05), transparent)',
    transition: 'left 0.6s ease',
  },
  
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    borderColor: 'rgba(42, 146, 53, 0.3)',
    boxShadow: `
      0 16px 48px rgba(42, 146, 53, 0.15),
      0 8px 24px rgba(0, 0, 0, 0.08)
    `,
    background: `
      linear-gradient(145deg, 
        rgba(255, 255, 255, 0.98) 0%, 
        rgba(240, 249, 242, 0.95) 100%
      )
    `,
    
    '&::before': {
      left: '100%',
    },
    
    '& .card-icon-wrapper': {
      transform: 'scale(1.1)',
      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
      '& .card-icon': {
        color: 'white',
        transform: 'scale(1.1)',
      }
    },
    
    '& .card-button': {
      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(42, 146, 53, 0.3)',
      
      '& .button-arrow': {
        transform: 'translateX(6px)',
      }
    }
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
  border: '2px solid rgba(42, 146, 53, 0.1)',
  animation: `${float} 6s ease-in-out infinite`,
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.3rem, 2.5vw, 1.6rem)',
  fontWeight: 700,
  color: '#1a1a1a',
  marginBottom: theme.spacing(2),
  letterSpacing: '-0.01em',
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  fontSize: '1rem',
  marginBottom: theme.spacing(4),
}));

const FeatureSection = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3, 2),
  borderRadius: 16,
  background: 'rgba(42, 146, 53, 0.03)',
  border: '1px solid rgba(42, 146, 53, 0.08)',
}));

const FeatureTag = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 20,
  backgroundColor: 'rgba(42, 146, 53, 0.1)',
  color: '#2A9235',
  fontSize: '0.75rem',
  fontWeight: 600,
  margin: theme.spacing(0.5),
  
  '& .MuiSvgIcon-root': {
    fontSize: 14,
    marginRight: theme.spacing(0.5),
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 14,
  padding: theme.spacing(1.5, 4),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.9) 0%, rgba(76, 175, 80, 0.9) 100%)',
  color: 'white',
  border: 'none',
  width: '100%',
  minHeight: 50,
  marginTop: 'auto',
  transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
  boxShadow: '0 6px 20px rgba(42, 146, 53, 0.2)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.5s ease',
  },
  
  '&:hover::before': {
    left: '100%',
  }
}));

const BackButton = styled(Button)(({ theme }) => ({
  color: '#2A9235',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  borderRadius: 12,
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.08)',
    transform: 'translateX(-4px)',
  }
}));

const PlatformIconsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 60,
  
  '& img': {
    height: 40,
    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))',
    transition: 'all 0.3s ease',
    
    '&:hover': {
      transform: 'scale(1.1)',
    }
  }
}));

const SchoolOptions: React.FC = () => {
  const navigate = useNavigate();
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Document URLs for privacy policy and terms
  const [documentUrls, setDocumentUrls] = useState<{
    privacyPolicy: string | null;
    termsAndConditions: string | null;
  }>({
    privacyPolicy: null,
    termsAndConditions: null
  });

  // Fetch policy documents from Firebase Storage
  const fetchPages = async () => {
    try {
      const storageRef = await ref(storage, 'pages');
      const result = await listAll(storageRef);
      const urls = await Promise.all(
        result.items.map((pages) => getDownloadURL(pages))
      );

      setDocumentUrls({
        termsAndConditions: urls[2] || null,
        privacyPolicy: urls[1] || null
      });
    } catch (e) {
      RemoteErrorLog(e, 'SchoolOptions.tsx');
    }
  };

  useEffect(() => {
    fetchPages();
    window.scrollTo(0, 0);
  }, []);

  // Navigation handlers
  const handleBack = () => {
    navigate('/signup');
  };

  const handleConnectClick = () => {
    navigate('/signup-school-sso');
  };

  const handleRegisterClick = () => {
    navigate('/signup-school-reg');
  };

  return (
    <>
      <Helmet>
        <title>School Setup Options - Spelling Hero</title>
        <meta name="description" content="Choose how your school wants to set up Spelling Hero. Connect with Clever/ClassLink or create a new school account." />
        <meta name="keywords" content="school setup, clever, classlink, school registration, educational platform" />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="School Setup Options - Spelling Hero" />
        <meta property="og:description" content="Choose how your school wants to set up Spelling Hero. Connect with Clever/ClassLink or create a new school account." />
        <meta property="og:type" content="website" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`${window.location.origin}/signup-school-select`} />
      </Helmet>

      <header>
        <Navbar currentPage="signup" />
      </header>

      <main className="main-container" style={{ paddingTop: 0 }}>
        <section className="quiz-signup">
          <Box sx={{ padding: 0 }}>
            <Grid container spacing={2}>
              <Grid
                item
                lg={5}
                xs={12}
                sm={12}
                sx={{ paddingLeft: 0 }}
                className="sidebar-top-spacing"
              >
                <Sidebar />
              </Grid>

              <Grid item xs={12} sm={12} lg={7} xl={7}>
                <Box sx={{
                  position: 'relative',
                  paddingLeft: { xs: 2, sm: 3, md: 4 },
                  paddingRight: { xs: 2, sm: 3, md: 4 },
                  paddingTop: 2,
                  paddingBottom: 4,
                  marginTop: { xs: 2, md: 4 }
                }}>
                  {isLoading ? (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      minHeight: '50vh' 
                    }}>
                      <CircularProgress size={60} />
                    </Box>
                  ) : (
                    <>
                      {/* Back Button - positioned close to sidebar (match SchoolRegistration) */}
                      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                        <BackButton
                          startIcon={<ArrowBackIcon />}
                          onClick={handleBack}
                          disabled={isLoading}
                        >
                          Back
                        </BackButton>
                      </Box>

                      <ContentContainer>
                        <Fade in={true} timeout={800}>
                          <HeaderSection>
                            <MainTitle variant="h2">
                              How does your school access apps?
                            </MainTitle>
                            <SubTitle variant="h5">
                              Choose the option that matches your school's setup
                            </SubTitle>
                          </HeaderSection>
                        </Fade>

                        <Grid container spacing={4}>
                          {/* Clever/ClassLink Option */}
                          <Grid item xs={12} md={6}>
                            <Grow in={true} timeout={1000}>
                              <OptionCard elevation={0}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'flex-start', 
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  mb: 2
                                }}>
                                  <IconWrapper className="card-icon-wrapper">
                                    <ConnectWithoutContactIcon 
                                      className="card-icon"
                                      sx={{ 
                                        fontSize: 40, 
                                        color: '#2A9235',
                                        transition: 'all 0.4s ease'
                                      }} 
                                    />
                                  </IconWrapper>
                                  <FeatureTag>
                                    <SpeedIcon />
                                    Quick Setup
                                  </FeatureTag>
                                </Box>
                                
                                <CardTitle variant="h3">
                                  We use Clever or ClassLink
                                </CardTitle>
                                
                                <CardDescription variant="body1">
                                  The fastest way to get started. Your students can use their existing school login credentials.
                                </CardDescription>

                                <FeatureSection>
                                  <PlatformIconsContainer>
                                    <img 
                                      src={cleverIcon} 
                                      alt="Clever" 
                                    />
                                    <img 
                                      src={classlinkIcon} 
                                      alt="ClassLink" 
                                    />
                                  </PlatformIconsContainer>
                                  
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    justifyContent: 'center',
                                    mt: 2
                                  }}>
                                    <FeatureTag>
                                      <GroupIcon />
                                      Auto roster sync
                                    </FeatureTag>
                                    <FeatureTag>
                                      <SecurityIcon />
                                      Secure login
                                    </FeatureTag>
                                  </Box>
                                </FeatureSection>
                                
                                <ActionButton 
                                  className="card-button"
                                  onClick={handleConnectClick}
                                  variant="contained" 
                                  size="large"
                                  endIcon={
                                    <ArrowForwardIcon 
                                      className="button-arrow"
                                      sx={{ 
                                        transition: 'transform 0.3s ease',
                                        fontSize: 18 
                                      }} 
                                    />
                                  }
                                >
                                  Connect & Sync
                                </ActionButton>
                              </OptionCard>
                            </Grow>
                          </Grid>

                          {/* Manual Setup Option */}
                          <Grid item xs={12} md={6}>
                            <Grow in={true} timeout={1200}>
                              <OptionCard elevation={0}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'flex-start', 
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  mb: 2
                                }}>
                                  <IconWrapper className="card-icon-wrapper">
                                    <SchoolIcon 
                                      className="card-icon"
                                      sx={{ 
                                        fontSize: 40, 
                                        color: '#2A9235',
                                        transition: 'all 0.4s ease'
                                      }} 
                                    />
                                  </IconWrapper>
                                  <FeatureTag>
                                    Flexible
                                  </FeatureTag>
                                </Box>
                                
                                <CardTitle variant="h3">
                                  No Personal Info Required
                                </CardTitle>
                                
                                <CardDescription variant="body1">
                                  No problem! We'll help you create a secure, private account for your school in just a few steps.
                                </CardDescription>

                                <FeatureSection>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    minHeight: 60
                                  }}>
                                    <SecurityIcon 
                                      sx={{ 
                                        fontSize: 40, 
                                        color: '#2A9235',
                                        mr: 2
                                      }} 
                                    />
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: '#2A9235',
                                        fontWeight: 600,
                                        fontSize: '1rem'
                                      }}
                                    >
                                      Secure & Private
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    justifyContent: 'center',
                                    mt: 2
                                  }}>
                                    <FeatureTag>
                                      Custom setup
                                    </FeatureTag>
                                    <FeatureTag>
                                      Full control
                                    </FeatureTag>
                                  </Box>
                                </FeatureSection>
                                
                                <ActionButton 
                                  className="card-button"
                                  onClick={handleRegisterClick}
                                  variant="contained" 
                                  size="large"
                                  endIcon={
                                    <ArrowForwardIcon 
                                      className="button-arrow"
                                      sx={{ 
                                        transition: 'transform 0.3s ease',
                                        fontSize: 18 
                                      }} 
                                    />
                                  }
                                >
                                  Create School Account
                                </ActionButton>
                              </OptionCard>
                            </Grow>
                          </Grid>
                        </Grid>
                      </ContentContainer>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default SchoolOptions;
